import React, { useState, useCallback, useEffect } from 'react';

const GOOGLE_FONT_URL =
  'https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Orbitron:wght@400;700&family=Rajdhani:wght@400;500;600&display=swap';

const styles = {
  fontLink: GOOGLE_FONT_URL,
  shell: {
    background: '#1a1a1a',
    borderRadius: '16px',
    padding: '20px',
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
    boxShadow: '0 0 0 1px #333, inset 0 1px 0 #444',
    fontFamily: 'Rajdhani, sans-serif',
  },
  brand: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '3px',
    color: '#555',
    textAlign: 'right',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  screenShell: {
    background: '#1c2b1c',
    borderRadius: '6px',
    padding: '14px 16px 10px',
    marginBottom: '16px',
    border: '2px solid #0d1a0d',
    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)',
  },
  screenLabel: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '2px',
    color: '#4a7a3a',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  screenValue: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '36px',
    color: '#a8e890',
    textAlign: 'right',
    minHeight: '44px',
    lineHeight: 1.1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  screenValueErr: {
    color: '#ff6666',
  },
  screenMode: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: '#4a7a3a',
    textAlign: 'right',
    marginTop: '4px',
    letterSpacing: '1px',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginBottom: '12px',
  },
  fieldCell: (isActive, isSolved, hasVal) => ({
    background: isActive ? '#1c2b1c' : '#1c1c1c',
    borderRadius: '6px',
    padding: '8px 10px',
    border: `1px solid ${isActive ? '#4a7a3a' : isSolved ? '#1e5a1e' : '#2a2a2a'}`,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  }),
  fieldLabel: (isActive) => ({
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '2px',
    color: isActive ? '#7ab86a' : '#555',
    textTransform: 'uppercase',
    marginBottom: '2px',
  }),
  fieldVal: (isActive, isSolved, hasVal) => ({
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '18px',
    color: isSolved ? '#7fe87f' : isActive ? '#a8e890' : hasVal ? '#ccc' : '#aaa',
    minHeight: '24px',
  }),
  infoCell: {
    background: '#1c1c1c',
    borderRadius: '6px',
    padding: '8px 10px',
    border: '1px solid #2a2a2a',
    display: 'flex',
    alignItems: 'center',
  },
  infoCellText: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '11px',
    color: '#444',
    lineHeight: 1.5,
  },
  btnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '6px',
    marginBottom: '8px',
  },
  btn: (variant, spanTwo) => ({
    background:
      variant === 'func' ? '#1e3a5f' :
      variant === 'action' ? '#5a3200' :
      variant === 'solve' ? '#1a4a1a' :
      variant === 'clear' ? '#3a1a1a' :
      variant === 'set' ? '#2a2a2a' :
      variant === 'bge' ? '#1e2a1e' :
      '#2d2d2d',
    border: 'none',
    borderRadius: '5px',
    padding: '12px 0',
    fontFamily: "'Orbitron', monospace",
    fontSize: variant === 'func' || variant === 'solve' || variant === 'clear' || variant === 'set' || variant === 'bge' ? '11px' : '13px',
    fontWeight: 400,
    color:
      variant === 'solve' ? '#a0ffa0' :
      variant === 'clear' ? '#ff8888' :
      variant === 'bge' ? '#aaccaa' :
      '#e0e0e0',
    cursor: 'pointer',
    boxShadow: '0 3px 0 #111',
    transition: 'transform 0.07s, box-shadow 0.07s',
    userSelect: 'none',
    lineHeight: 1,
    letterSpacing: variant === 'func' || variant === 'solve' ? '0.5px' : undefined,
    gridColumn: spanTwo ? 'span 2' : undefined,
  }),
  note: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12px',
    color: '#444',
    textAlign: 'center',
    letterSpacing: '1px',
    marginTop: '8px',
  },
};

function fmt(n) {
  if (n === null) return '—';
  if (Math.abs(n) >= 1e9) return n.toExponential(4);
  return parseFloat(n.toFixed(6)).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function fvFormula(n, r, pv, pmt, bge) {
  if (r === 0) return -(pv + pmt * n);
  const f = Math.pow(1 + r, n);
  const adj = bge ? (1 + r) : 1;
  return -(pv * f + pmt * adj * (f - 1) / r);
}

function pvFormula(n, r, pmt, fv, bge) {
  if (r === 0) return -(pmt * n + fv);
  const f = Math.pow(1 + r, n);
  const adj = bge ? (1 + r) : 1;
  return -(pmt * adj * (f - 1) / r + fv) / f;
}

function pmtFormula(n, r, pv, fv, bge) {
  if (r === 0) return -(pv + fv) / n;
  const f = Math.pow(1 + r, n);
  const adj = bge ? (1 + r) : 1;
  return -(pv * f + fv) * r / (f - 1) / adj;
}

function solveFor(target, vals, isEnd) {
  const { N, I, PV, PMT, FV } = vals;
  const r = (I || 0) / 100;
  const bge = !isEnd;

  if (target === 'FV') return fvFormula(N, r, PV, PMT, bge);
  if (target === 'PV') return pvFormula(N, r, PMT, FV, bge);
  if (target === 'PMT') return pmtFormula(N, r, PV, FV, bge);
  if (target === 'N') {
    if (r === 0) return -(PV + FV) / PMT;
    const adj = bge ? (1 + r) : 1;
    const num = -FV * r + PMT * adj;
    const den = PV * r + PMT * adj;
    if (num / den <= 0) throw new Error('No real solution');
    return Math.log(num / den) / Math.log(1 + r);
  }
  if (target === 'I') {
    let lo = -0.9999, hi = 100, mid = 0;
    for (let i = 0; i < 200; i++) {
      mid = (lo + hi) / 2;
      const guess = fvFormula(N, mid, PV, PMT, bge);
      if (Math.abs(guess - FV) < 0.0001) return mid * 100;
      if (guess > FV) hi = mid; else lo = mid;
    }
    return mid * 100;
  }
}

const FIELDS = ['N', 'I', 'PV', 'PMT', 'FV'];
const FIELD_LABELS = { N: 'N · Periods', I: 'I/Y · Rate %', PV: 'PV · Present Val', PMT: 'PMT · Payment', FV: 'FV · Future Val' };

// Maps a keyboard key to the button `id` it corresponds to, so keyboard
// input can trigger the same press-flash style as a click/tap.
const KEY_TO_BTN_ID = {
  '0': 'd0', '1': 'd1', '2': 'd2', '3': 'd3', '4': 'd4',
  '5': 'd5', '6': 'd6', '7': 'd7', '8': 'd8', '9': 'd9',
  '.': 'dot', Backspace: 'bsp', '-': 'sign', '+': 'sign',
};

// IMPORTANT: Btn lives at module scope (outside FinancialCalculator) so its
// function identity never changes between renders. If it were declared
// inside the component (as it was before), every state update would create
// a *new* Btn function, and React would unmount + remount every button to
// match — which is what made onMouseDown/onTouchStart break clicks: the
// button got destroyed mid-press, before the click event could fire on it.
const Btn = React.memo(function Btn({ id, variant = 'num', spanTwo, isPressed, onPressStart, onClick, children }) {
  const style = {
    ...styles.btn(variant, spanTwo),
    transform: isPressed ? 'translateY(2px)' : 'none',
    boxShadow: isPressed ? '0 1px 0 #111' : '0 3px 0 #111',
  };

  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      onMouseDown={onPressStart}
      onTouchStart={onPressStart}
    >
      {children}
    </button>
  );
});

function FinancialCalculator() {
  const [vals, setVals] = useState({ N: null, I: null, PV: null, PMT: null, FV: null });
  const [active, setActive] = useState('N');
  const [input, setInput] = useState("");
  const [display, setDisplay] = useState('0');
  const [modeLine, setModeLine] = useState('SELECT VARIABLE · ENTER VALUE · COMPUTE');
  const [isEnd, setIsEnd] = useState(true);
  const [solvedField, setSolvedField] = useState(null);
  const [isErr, setIsErr] = useState(false);
  const [pressed, setPressed] = useState(null);

  // Stable reference (useCallback) so it's safe to use as a dependency
  // in the keydown effect below without re-attaching the listener every render.
  const flashPress = useCallback((id) => {
    setPressed(id);
    setTimeout(() => setPressed(null), 120);
  }, []);

  const selectField = useCallback((k) => {
    setActive(k);
    setInput('');
    setIsErr(false);
    setDisplay(vals[k] !== null ? fmt(vals[k]) : '0');
    setModeLine(`EDITING ${k} · PRESS DIGITS THEN STORE`);
  }, [vals]);

  const digit = useCallback((d) => {
    setInput(prev => {
      if (d === '.' && prev.includes('.')) return prev;
      if (prev.length > 14) return prev;
      if (d === '.' && prev === '') return '0.';
      if (d !== '.' && prev === '0') return d;
      if (d !== '.' && prev === '-0') return '-' + d;
      return prev + d;
    });
    setIsErr(false);
  }, []);

  const toggleSign = useCallback(() => {
    setInput(prev => {
      if (prev.startsWith('-')) return prev.slice(1);
      if (prev !== '' && prev !== '0') return '-' + prev;
      return prev;
    });
    if (input === '' || input === '0') {
      setVals(prev => {
        if (prev[active] !== null) {
          const next = { ...prev, [active]: -prev[active] };
          setDisplay(fmt(-prev[active]));
          return next;
        }
        return prev;
      });
    }
  }, [input, active]);

  const backspace = useCallback(() => {
    
    setInput(prev => prev.slice(0, -1) || '0');
    
    setIsErr(false);
  }, []);

  const storeField = useCallback((k) => {
    setActive(k);
    setInput(prev => {
      if (prev !== '') {
        const num = parseFloat(prev);
        setVals(v => ({ ...v, [k]: num }));
        setDisplay(fmt(num));
        setModeLine(`${k} STORED · SELECT NEXT VARIABLE`);
      } else {
        setDisplay(vals[k] !== null ? fmt(vals[k]) : '0');
        setModeLine(`EDITING ${k} · PRESS DIGITS THEN STORE`);
      }
      return '';
    });
    setSolvedField(null);
  }, [vals]);

  const clearEntry = useCallback(() => {
    setInput('');
    setDisplay('0');
    setIsErr(false);
  }, []);

  const clearAll = useCallback(() => {
    setVals({ N: null, I: null, PV: null, PMT: null, FV: null });
    setInput('');
    setActive('N');
    setSolvedField(null);
    setDisplay('0');
    setModeLine('ALL CLEARED · SELECT VARIABLE');
    setIsErr(false);
  }, []);

  const compute = useCallback(() => {
    const currentVals = { ...vals };
    if (input !== '') {
      currentVals[active] = parseFloat(input);
      setVals(currentVals);
      setInput('');
    }

    const known = Object.entries(currentVals).filter(([, v]) => v !== null);
    const nulls = Object.entries(currentVals).filter(([, v]) => v === null);

    if (known.length < 4 || nulls.length !== 1) {
      setDisplay('ERR');
      setIsErr(true);
      setModeLine(nulls.length === 0 ? 'ALL SET — CLEAR ONE TO SOLVE' : 'NEED EXACTLY 4 KNOWN VALUES');
      return;
    }

    const target = nulls[0][0];
    try {
      const result = solveFor(target, currentVals, isEnd);
      if (!isFinite(result) || isNaN(result)) throw new Error();
      setVals({ ...currentVals, [target]: result });
      setSolvedField(target);
      setDisplay(fmt(result));
      setModeLine(`${target} = ${fmt(result)}`);
      setIsErr(false);
    } catch {
      setDisplay('ERR');
      setIsErr(true);
      setModeLine('NO REAL SOLUTION');
    }
  }, [vals, input, active, isEnd]);

  const toggleBGE = useCallback(() => {
    setIsEnd(prev => {
      setModeLine(prev ? 'BEGIN MODE SET' : 'END MODE SET');
      return !prev;
    });
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = GOOGLE_FONT_URL;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    if (input !== '') setDisplay(input);
  }, [input]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Flash the matching on-screen button for any mapped key, so keyboard
      // input gets the same visual feedback as clicking/tapping.
      const btnId = KEY_TO_BTN_ID[event.key];
      if (btnId) flashPress(btnId);

      event.preventDefault();

      switch (event.key) {
        case '0': case '1': case '2': case '3': case '4':
        case '5': case '6': case '7': case '8': case '9':
        case '.':
          digit(event.key);
          break;
        case 'Backspace':
          backspace();
          break;
        case '-':
        case '+':
          toggleSign();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [flashPress, digit, backspace, toggleSign]);

  // Small helper so each <Btn> below doesn't need to repeat
  // `isPressed={pressed === id}` / `onPressStart={() => flashPress(id)}` by hand.
  const pressProps = (id) => ({
    isPressed: pressed === id,
    onPressStart: () => flashPress(id),
  });

  return (
    <div style={styles.shell}>
      <div style={styles.brand}>Financial Calc · TVM</div>

      <div style={styles.screenShell}>
        <div style={styles.screenLabel}>Display</div>
        <div style={{ ...styles.screenValue, ...(isErr ? styles.screenValueErr : {}) }}>
          {display}
        </div>
        <div style={styles.screenMode}>{modeLine}</div>
      </div>

      <div style={styles.fieldsGrid}>
        {FIELDS.map(k => {
          const isActive = active === k;
          const isSolved = solvedField === k;
          const hasVal = vals[k] !== null;
          return (
            <div
              key={k}
              style={styles.fieldCell(isActive, isSolved, hasVal)}
              onClick={() => selectField(k)}
            >
              <div style={styles.fieldLabel(isActive)}>{FIELD_LABELS[k]}</div>
              <div style={styles.fieldVal(isActive, isSolved, hasVal)}>
                {hasVal ? fmt(vals[k]) : '—'}
              </div>
            </div>
          );
        })}
        <div style={styles.infoCell}>
          <div style={styles.infoCellText}>
            Set 4 known<br />values, then<br />tap COMPUTE
          </div>
        </div>
      </div>

      <div style={styles.btnGrid}>
        {/* Row 1: TVM store buttons */}
        {FIELDS.map(k => (
          <Btn key={k} id={`store-${k}`} variant="func" {...pressProps(`store-${k}`)} onClick={() => storeField(k)}>
            {k === 'I' ? 'I/Y' : k}
          </Btn>
        ))}

        {/* Row 2 */}
        <Btn id="sign" variant="func" {...pressProps('sign')} onClick={toggleSign}>+/−</Btn>
        <Btn id="d7" {...pressProps('d7')} onClick={() => digit('7')}>7</Btn>
        <Btn id="d8" {...pressProps('d8')} onClick={() => digit('8')}>8</Btn>
        <Btn id="d9" {...pressProps('d9')} onClick={() => digit('9')}>9</Btn>
        <Btn id="ce" variant="clear" {...pressProps('ce')} onClick={clearEntry}>CE</Btn>

        {/* Row 3 */}
        <Btn id="cpt1" variant="solve" {...pressProps('cpt1')} onClick={compute}>CPT</Btn>
        <Btn id="d4" {...pressProps('d4')} onClick={() => digit('4')}>4</Btn>
        <Btn id="d5" {...pressProps('d5')} onClick={() => digit('5')}>5</Btn>
        <Btn id="d6" {...pressProps('d6')} onClick={() => digit('6')}>6</Btn>
        <Btn id="clr" variant="action" {...pressProps('clr')} onClick={clearAll}>CLR</Btn>

        {/* Row 4 */}
        <Btn id="set" variant="set" {...pressProps('set')} onClick={() => storeField(active)}>SET</Btn>
        <Btn id="d1" {...pressProps('d1')} onClick={() => digit('1')}>1</Btn>
        <Btn id="d2" {...pressProps('d2')} onClick={() => digit('2')}>2</Btn>
        <Btn id="d3" {...pressProps('d3')} onClick={() => digit('3')}>3</Btn>
        <Btn id="bsp" variant="action" {...pressProps('bsp')} onClick={backspace}>⌫</Btn>

        {/* Row 5 */}
        <Btn id="bge" variant="bge" {...pressProps('bge')} onClick={toggleBGE}>BEG/END</Btn>
        <Btn id="d0" spanTwo {...pressProps('d0')} onClick={() => digit('0')}>0</Btn>
        <Btn id="dot" {...pressProps('dot')} onClick={() => digit('.')}>.</Btn>
        <Btn id="cpt2" variant="solve" {...pressProps('cpt2')} onClick={compute}>CPT</Btn>
      </div>

      <div style={styles.note}>
        {isEnd ? 'END MODE (payments at period end)' : 'BEGIN MODE (payments at period start)'}
      </div>
    </div>
  );
}

export default FinancialCalculator;
